import React, { useState, useRef, useEffect } from 'react';
import { 
  IconButton, 
  Dialog, 
  DialogContent, 
  DialogTitle,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Tooltip
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface AudioRecorderProps {
  onRecordingComplete: (audioFile: File) => void;
  disabled?: boolean;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onRecordingComplete, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Limpar timer ao desmontar
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Solicitar permissão de áudio
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Configurar MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        setIsProcessing(true);
        
        // Criar blob do áudio
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        
        // Converter para arquivo
        const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
        const fileName = `audio_${Date.now()}.${extension}`;
        const audioFile = new File([audioBlob], fileName, { type: mimeType });
        
        // Parar todas as tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Chamar callback com o arquivo
        onRecordingComplete(audioFile);
        
        // Resetar estado
        setIsProcessing(false);
        setDialogOpen(false);
        setRecordingTime(0);
        chunksRef.current = [];
      };
      
      // Iniciar gravação
      mediaRecorder.start(1000); // Capturar dados a cada 1 segundo
      setIsRecording(true);
      setDialogOpen(true);
      
      // Iniciar timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      alert('Erro ao acessar o microfone. Verifique as permissões do navegador.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      
      // Parar tracks do stream
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsRecording(false);
    setDialogOpen(false);
    setRecordingTime(0);
    chunksRef.current = [];
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Tooltip title="Gravar áudio">
        <span>
          <IconButton
            color="primary"
            onClick={startRecording}
            disabled={disabled || isRecording}
            size="large"
          >
            <MicIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog 
        open={dialogOpen} 
        onClose={cancelRecording}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {isProcessing ? 'Processando áudio...' : 'Gravando áudio'}
            </Typography>
            {!isProcessing && (
              <IconButton onClick={cancelRecording} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            gap={3}
            py={2}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={80} />
                <Typography variant="body1" color="text.secondary">
                  Preparando seu áudio...
                </Typography>
              </>
            ) : (
              <>
                {/* Ícone de microfone animado */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                        opacity: 0.8,
                      },
                      '100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <MicIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>

                {/* Tempo de gravação */}
                <Typography variant="h4" fontWeight="bold">
                  {formatTime(recordingTime)}
                </Typography>

                {/* Barra de progresso animada */}
                <Box width="100%">
                  <LinearProgress 
                    variant="indeterminate" 
                    color="error"
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                </Box>

                {/* Instruções */}
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  textAlign="center"
                >
                  Fale agora... Clique em parar quando terminar.
                </Typography>

                {/* Botões */}
                <Box display="flex" gap={2} mt={2}>
                  <IconButton
                    onClick={cancelRecording}
                    size="large"
                    sx={{
                      backgroundColor: 'grey.300',
                      '&:hover': {
                        backgroundColor: 'grey.400',
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  
                  <IconButton
                    onClick={stopRecording}
                    size="large"
                    sx={{
                      backgroundColor: 'success.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'success.dark',
                      },
                    }}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Cancelar / Parar e enviar
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AudioRecorder;



